/**
 * ReleaseNotesModal - Modal que muestra las novedades de la nueva versión
 */

import React, { useState, useEffect } from 'react';
import {
  Wifi,
  Server,
  Send,
  Download,
  Sparkles,
  X,
  ArrowRight,
  Calendar,
  Rocket,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  RELEASE_NOTES,
  APP_VERSION,
  formatReleaseDate,
  hasSeenReleaseNotes,
  markReleaseNotesSeen,
  isV2Released,
} from '@/lib/featureFlags';

// Map icon names to components
const iconMap = {
  Wifi: Wifi,
  Server: Server,
  Send: Send,
  Download: Download,
};

export default function ReleaseNotesModal({ onExplore }) {
  const [open, setOpen] = useState(false);
  
  useEffect(() => {
    // Solo mostrar si ya pasó la fecha de lanzamiento y no lo ha visto
    if (isV2Released() && !hasSeenReleaseNotes()) {
      // Pequeño delay para que no aparezca inmediatamente
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const handleClose = () => {
    markReleaseNotesSeen();
    setOpen(false);
  };
  
  const handleExplore = () => {
    markReleaseNotesSeen();
    setOpen(false);
    if (onExplore) onExplore();
  };
  
  const latestRelease = RELEASE_NOTES[0];
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg bg-gradient-to-b from-[#111111] to-[#0A0A0A] border-white/10 p-0 overflow-hidden">
        {/* Header con gradiente */}
        <div className="relative bg-gradient-to-r from-[#34C759]/20 via-[#007AFF]/20 to-[#AF52DE]/20 p-6 pb-8">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#34C759] to-[#007AFF] flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#FFCC00]" />
                AVX RF PRO v{APP_VERSION}
              </h2>
              <p className="text-sm text-white/60 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatReleaseDate()}
              </p>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold text-white">
            {latestRelease.title}
          </h3>
        </div>
        
        {/* Features */}
        <div className="p-6 space-y-4">
          {latestRelease.highlights.map((feature, index) => {
            const IconComponent = iconMap[feature.icon] || Sparkles;
            return (
              <div
                key={index}
                className="flex items-start gap-4 p-3 rounded-lg bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.05] transition-colors"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <IconComponent
                    className="w-5 h-5"
                    style={{ color: feature.color }}
                  />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">{feature.title}</h4>
                  <p className="text-sm text-[#71717A]">{feature.description}</p>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Footer */}
        <div className="p-6 pt-2 flex gap-3">
          <Button
            variant="ghost"
            onClick={handleClose}
            className="flex-1 text-[#71717A] hover:text-white"
          >
            Más tarde
          </Button>
          <Button
            onClick={handleExplore}
            className="flex-1 bg-gradient-to-r from-[#34C759] to-[#007AFF] hover:opacity-90 text-white"
          >
            Explorar Novedades
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
